import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Event color configurations matching Untitled UI design
 * More vibrant colors as seen in the screenshot
 */
export const EVENT_COLORS = {
  blue: {
    bg: 'bg-blue-100',
    text: 'text-blue-700',
    time: 'text-blue-600'
  },
  pink: {
    bg: 'bg-pink-100',
    text: 'text-pink-700',
    time: 'text-pink-600'
  },
  green: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-700',
    time: 'text-emerald-600'
  },
  orange: {
    bg: 'bg-orange-100',
    text: 'text-orange-700',
    time: 'text-orange-600'
  },
  purple: {
    bg: 'bg-purple-100',
    text: 'text-purple-700',
    time: 'text-purple-600'
  },
  red: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    time: 'text-red-600'
  },
  cyan: {
    bg: 'bg-cyan-100',
    text: 'text-cyan-700',
    time: 'text-cyan-600'
  },
  amber: {
    bg: 'bg-amber-100',
    text: 'text-amber-700',
    time: 'text-amber-600'
  },
  gray: {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    time: 'text-gray-600'
  }
};

/**
 * Color options for the event editor with display values
 */
export const EVENT_COLOR_OPTIONS = [
  { key: 'blue', label: 'Blue', hex: '#3b82f6', bgClass: 'bg-blue-500' },
  { key: 'pink', label: 'Pink', hex: '#ec4899', bgClass: 'bg-pink-500' },
  { key: 'green', label: 'Green', hex: '#10b981', bgClass: 'bg-emerald-500' },
  { key: 'orange', label: 'Orange', hex: '#f97316', bgClass: 'bg-orange-500' },
  { key: 'purple', label: 'Purple', hex: '#a855f7', bgClass: 'bg-purple-500' },
  { key: 'red', label: 'Red', hex: '#ef4444', bgClass: 'bg-red-500' },
  { key: 'cyan', label: 'Cyan', hex: '#06b6d4', bgClass: 'bg-cyan-500' },
  { key: 'amber', label: 'Amber', hex: '#f59e0b', bgClass: 'bg-amber-500' },
  { key: 'gray', label: 'Gray', hex: '#6b7280', bgClass: 'bg-gray-500' }
];

/**
 * Format time for display (e.g., "9:00 am", "2:30 pm")
 */
export const formatEventTime = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const hours = d.getHours();
  const minutes = d.getMinutes();

  // Don't show time if it's midnight (likely means no time was set)
  if (hours === 0 && minutes === 0) return '';

  const ampm = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, '0');
  return `${displayHours}:${displayMinutes} ${ampm}`;
};

/**
 * CalendarEvent - A single event pill/badge for the calendar
 * Matches Untitled UI design - rounded pill with title and time
 *
 * @param {Object} props
 * @param {string} props.title - Event title
 * @param {Date|string} props.startTime - Event start time
 * @param {string} props.color - Color key from EVENT_COLORS
 * @param {boolean} props.isMultiDay - Whether this is a multi-day event
 * @param {boolean} props.isStart - Whether this is the start of a multi-day event
 * @param {boolean} props.isEnd - Whether this is the end of a multi-day event
 * @param {boolean} props.isMiddle - Whether this is a middle day of a multi-day event
 * @param {function} props.onClick - Click handler
 * @param {string} props.className - Additional classes
 */
export function CalendarEvent({
  title,
  startTime,
  color = 'blue',
  isMultiDay = false,
  isStart = false,
  isEnd = false,
  isMiddle = false,
  onClick,
  className
}) {
  const colorConfig = EVENT_COLORS[color] || EVENT_COLORS.blue;
  const timeDisplay = startTime ? formatEventTime(startTime) : null;

  // Multi-day event styling
  if (isMultiDay) {
    return (
      <button
        onClick={onClick}
        className={cn(
          'w-full text-left text-xs font-medium px-2 py-1 truncate transition-opacity hover:opacity-80',
          colorConfig.bg,
          colorConfig.text,
          // Rounded corners based on position
          isStart && 'rounded-l-md',
          isEnd && 'rounded-r-md',
          isMiddle && 'rounded-none',
          !isStart && !isEnd && !isMiddle && 'rounded-md',
          className
        )}
      >
        {isStart ? title : ''}
      </button>
    );
  }

  // Single day event - Untitled UI style (no left border)
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center justify-between gap-1 text-left text-xs px-2 py-1 rounded-md transition-opacity hover:opacity-80',
        colorConfig.bg,
        className
      )}
    >
      <span className={cn('font-medium truncate flex-1', colorConfig.text)}>
        {title}
      </span>
      {timeDisplay && (
        <span className={cn('text-[10px] shrink-0', colorConfig.time)}>
          {timeDisplay}
        </span>
      )}
    </button>
  );
}

/**
 * CalendarEventOverflow - Shows "+N more..." indicator
 */
export function CalendarEventOverflow({ count, onClick }) {
  if (count <= 0) return null;

  return (
    <button
      onClick={onClick}
      className="w-full text-left text-xs text-gray-500 hover:text-gray-700 px-2 py-0.5 transition-colors"
    >
      {count} more...
    </button>
  );
}

export default CalendarEvent;
